import { objectId } from "@shared/util/object-id"
import type { AttachmentSource, IdentifiedLink } from "@tiptap-content/extensions/link/link-attrs"

/**
 * Fresh Link attrs for an uploaded/picked file (docs/unified-links.md §6): a block
 * token (an attachment line), with the upload fields filled from the Filestack result
 * and the insert path's provenance persisted as `source` (§7). A new attachment
 * identity is minted.
 */
export function linkFromUpload(
  file: { url: string; filename: string; mimetype?: string; size?: number },
  source: AttachmentSource,
): IdentifiedLink {
  return {
    attachmentId: objectId(),
    url: file.url,
    title: file.filename,
    type: file.mimetype?.startsWith("image/") ? "image" : "file",
    mimetype: file.mimetype ?? null,
    size: file.size ?? null,
    isWriteable: false,
    icon: null,
    source,
    renderAs: "block",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  }
}

/** A file chosen from the Google Drive picker (its `docs` entries, normalized). */
export interface GoogleDrivePickedFile {
  /** The file's display name (`doc.name`). */
  title: string
  url: string
  /** The picker's iconUrl (`doc.iconUrl`), kept as the Link provenance hint. */
  icon: string | null
  /** "image" for a picked image, "file" for everything else. */
  type: "image" | "file"
}

/**
 * Fresh Link attrs for a Google Drive pick (docs/unified-links.md §6): a block token
 * whose fields come from the picker result, with "google" persisted as `source` (§7) and
 * the picker's icon kept as the provenance hint. A new attachment identity is minted.
 */
export function linkFromGooglePick(file: GoogleDrivePickedFile): IdentifiedLink {
  return {
    attachmentId: objectId(),
    url: file.url,
    title: file.title,
    type: file.type,
    mimetype: null,
    size: null,
    isWriteable: false,
    icon: file.icon,
    source: "google",
    renderAs: "block",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  }
}
